import { LogEntryWithType, CallLog, FormGroup } from '@/types/TInbox';
import { isResponseValid, safeJSONParse } from '@/utils/helper';

export const processIntakeForms = (logs: LogEntryWithType[]): FormGroup[] => {
	const formLogs = logs.filter((log) => log.type === 'form');
	const groups: { [key: string]: FormGroup } = {};
	let formCounter = 1;

	console.log('Processing intake forms from logs:', formLogs.length);

	formLogs.forEach((log) => {
		const callData = log.originalLog as CallLog;

		if (!callData.intakeResponses) {
			console.log('No intakeResponses for log:', log.id);
			return;
		}

		try {
			const parsed = safeJSONParse(callData.intakeResponses, []);

			const intakeResponses = Array.isArray(parsed) ? parsed : [parsed];
			const metadata = safeJSONParse(callData?.intakeMetadata, {});
			intakeResponses.forEach((form, formIndex) => {
				const questions = Object.keys(form);
				const key = questions.join('~~~'); // Same key format as your original code

				console.log('Processing form with questions:', questions);

				if (isResponseValid(form)) {
					if (!groups[key]) {
						groups[key] = {
							id: `form-${formCounter++}`,
							title: `Form ${Object.keys(groups).length + 1}`,
							description: 'Intake form responses',
							type: 'simple',
							questions,
							responses: [],
						};
					}

					// Add response
					groups[key].responses.push({
						id: `${log.id}_${formIndex}`,
						date: log.date,
						phone: log.contact.phone,
						name: log.contact.name,
						answers: form,
						transcript: callData.transcript || '',
						log,
						completed_at: metadata.completed_at || null,
						note: metadata.note || '',
					});
				}
			});
		} catch (error) {
			console.error('Error occured:', error);
		}
	});

	console.log('Processed form groups:', Object.keys(groups).length);
	return Object.values(groups).filter((group) => group.responses.length > 0);
};

export const processIntakeFormsFromSingleLog = (log: LogEntryWithType): FormGroup[] => {
	const callData = log.originalLog as CallLog;
	const forms: FormGroup[] = [];
	let formCounter = 1;
	const metadata = safeJSONParse(callData?.intakeMetadata, {});

	// Process simple intake_questions
	if (callData.intake_questions) {
		try {
			const questions = safeJSONParse(callData.intake_questions, []);
			if (Array.isArray(questions) && questions.length > 0) {
				const form: FormGroup = {
					id: `simple-form-${formCounter++}`,
					title: 'Standard Intake Form',
					description: 'Basic intake questions',
					type: 'simple',
					questions,
					responses: [],
				};

				// Add response if intakeResponses exists
				if (callData.intakeResponses) {
					try {
						const responses = safeJSONParse(callData.intakeResponses, []);
						if (isResponseValid(responses)) {
							form.responses.push({
								id: log.id,
								date: log.date,
								phone: log.contact.phone,
								name: log.contact.name,
								answers: responses,
								transcript: callData.transcript || '',
								log,
								completed_at: metadata.completed_at || null,
								note: metadata.note || '',
							});
						}
					} catch (error) {
						console.error('Error parsing intakeResponses for simple form:', error);
					}
				}

				if (form.responses.length > 0) {
					forms.push(form);
				}
			}
		} catch (error) {
			console.error('Error parsing intake_questions:', error);
		}
	}

	// Process workflow intake_questions_workflows
	if (callData.intake_questions_workflows) {
		try {
			const workflows = safeJSONParse(callData.intake_questions_workflows, []);
			if (Array.isArray(workflows)) {
				workflows.forEach((workflow, workflowIndex) => {
					if (
						workflow.questions &&
						Array.isArray(workflow.questions) &&
						workflow.questions.length > 0
					) {
						const form: FormGroup = {
							id: `workflow-form-${workflowIndex}`,
							title: workflow.customScenario || `Workflow Form ${workflowIndex + 1}`,
							description: workflow.customScenario || 'Workflow-based intake questions',
							type: 'workflow',
							questions: workflow.questions,
							scenario: workflow.customScenario,
							prompt: workflow.postExecutionPrompt,
							responses: [],
						};

						// Add response if intakeResponses exists
						if (callData.intakeResponses) {
							try {
								const responses = safeJSONParse(callData.intakeResponses, {});

								// Filter responses that match this workflow's questions
								const workflowResponses: { [key: string]: string } = {};
								workflow.questions.forEach((question: string) => {
									if (responses[question] !== undefined) {
										workflowResponses[question] = responses[question];
									}
								});

								// Only add if we have responses for this workflow's questions
								if (Object.keys(workflowResponses).length > 0) {
									if (isResponseValid(workflowResponses)) {
										form.responses.push({
											id: `${log.id}_wf_${workflowIndex}`,
											date: log.date,
											phone: log.contact.phone,
											name: log.contact.name,
											answers: workflowResponses,
											transcript: callData.transcript || '',
											log,
											completed_at: metadata.completed_at || null,
											note: metadata.note || '',
										});
									}
								}
							} catch (error) {
								console.error('Error parsing intakeResponses for workflow:', error);
							}
						}

						if (form.responses.length > 0) {
							forms.push(form);
						}
					}
				});
			}
		} catch (error) {
			console.error('Error parsing intake_questions_workflows:', error);
		}
	}

	return forms;
};
